import React, { useState } from 'react';
import TelefoneBrasileiroInput from 'react-telefone-brasileiro';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiCheckCircle,
    FiAlertCircle,
    FiMail,
    FiMessageSquare,
    FiPhone,
    FiUser
} from 'react-icons/fi';

const API_URL =
    (process.env.REACT_APP_API_URL || 'http://localhost:3333') + '/api/public/complaints';

function mapMotivoToCategory(motivo) {
    switch (motivo) {
        case 'Reclamações':
            return 'RECLAMACAO';
        case 'Elogios':
            return 'ELOGIO';
        case 'Informações':
        case 'Comercial':
        case 'Parcerias':
        case 'Outros':
        default:
            return 'SUGESTAO';
    }
}

function ContactForm() {
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [motivo, setMotivo] = useState('');
    const [menssage, setMessage] = useState('');
    const [messageError, setError] = useState('');
    const [resultTrue, setResultTrue] = useState('');
    const [checkbox, setCheck] = useState(false);
    const [telephone, setTelephone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [touched, setTouched] = useState({});

    function handlerTimeout(state, count) {
        setTimeout(() => {
            state('');
        }, count);
    }

    function normalizeDigits(value = '') {
        return String(value).replace(/\D/g, '');
    }

    const validation = {
        nome: nome.trim().length >= 3,
        email: /\S+@\S+\.\S+/.test(email),
        motivo: !!motivo && motivo !== 'selected',
        menssage: menssage.trim().length >= 10,
        telephone: telephone ? normalizeDigits(telephone).length >= 10 : true,
        checkbox: checkbox,
    };

    function markTouched(fieldName) {
        setTouched((prev) => ({ ...prev, [fieldName]: true }));
    }

    function getFieldState(fieldName) {
        if (!touched[fieldName]) return {};
        const isValid = validation[fieldName];
        return {
            border: `1px solid ${isValid ? 'rgba(25, 135, 84, 0.45)' : 'rgba(220, 53, 69, 0.45)'}`,
            boxShadow: isValid
                ? '0 0 0 0.2rem rgba(25, 135, 84, 0.08)'
                : '0 0 0 0.2rem rgba(220, 53, 69, 0.08)',
        };
    }

    function validateBeforeSubmit() {
        setTouched({
            nome: true,
            email: true,
            motivo: true,
            menssage: true,
            telephone: true,
            checkbox: true,
        });

        if (!validation.nome) return 'O campo nome precisa ser preenchido corretamente.';
        if (!validation.email) return 'Informe um e-mail válido.';
        if (!validation.telephone) return 'Informe um telefone válido ou deixe o campo em branco.';
        if (!validation.motivo) return 'Selecione o motivo do contato.';
        if (!validation.menssage) return 'A mensagem precisa ter pelo menos 10 caracteres.';
        if (!validation.checkbox) return 'Você precisa concordar com os termos e a política de privacidade.';
        return '';
    }

    function resetForm() {
        setNome('');
        setEmail('');
        setMotivo('');
        setMessage('');
        setCheck(false);
        setTelephone('');
        setTouched({});
    }

    async function handlerSendContact(e) {
        e.preventDefault();
        setError('');
        setResultTrue('');

        const validationError = validateBeforeSubmit();
        if (validationError) {
            setError(validationError);
            handlerTimeout(setError, 5000);
            return;
        }

        setIsSubmitting(true);

        try {
            const category = mapMotivoToCategory(motivo);

            const payload = {
                category,
                nome: nome.trim(),
                email: email.trim(),
                ocorrencia: motivo.trim(),
                relacao: telephone
                    ? `Contato do site - telefone: ${telephone}`
                    : 'Contato do site',
                denunciadoNome: null,
                relato: menssage.trim(),
                identificado: true,
                privacyAccepted: checkbox,
                lgpdAccepted: checkbox,
            };

            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            let data = null;
            try {
                data = await response.json();
            } catch (jsonError) {
                data = null;
            }

            if (!response.ok) {
                const apiMessage =
                    data?.message ||
                    data?.error ||
                    'Não foi possível enviar sua mensagem no momento. Tente novamente.';
                throw new Error(apiMessage);
            }

            setResultTrue('Sua mensagem foi enviada com sucesso!');
            resetForm();
            handlerTimeout(setResultTrue, 6000);
        } catch (err) {
            console.error('Erro ao enviar contato:', err);
            setError(
                err.message || 'Não foi possível enviar sua mensagem no momento. Tente novamente.'
            );
            handlerTimeout(setError, 6000);
        } finally {
            setIsSubmitting(false);
        }
    }

    const inputBaseStyle = {
        borderRadius: '14px',
        minHeight: '52px',
        background: '#ffffff',
        border: '1px solid rgba(13, 46, 99, 0.10)',
        padding: '12px 14px',
        color: '#10213a',
        transition: 'all 0.25s ease',
    };

    const labelStyle = {
        fontWeight: 700,
        fontSize: '0.94rem',
        color: '#0d2e63',
        marginBottom: '10px',
        display: 'block',
    };

    const sideCardStyle = {
        background: '#ffffff',
        borderRadius: '24px',
        padding: '24px',
        boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
        border: '1px solid rgba(13, 46, 99, 0.06)',
    };

    return (
        <section style={{ padding: '0' }} id="form5-1v">
            <div className="container">
                <form onSubmit={handlerSendContact}>
                    <div className="row g-4 align-items-start">
                        <div className="col-lg-8">
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '24px',
                                    padding: '26px',
                                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                                    border: '1px solid rgba(13, 46, 99, 0.06)',
                                }}
                            >
                                <div className="row g-4">
                                    <div className="col-12">
                                        <div
                                            style={{
                                                fontSize: '1.05rem',
                                                fontWeight: 800,
                                                color: '#0d2e63',
                                                marginBottom: '4px',
                                            }}
                                        >
                                            Dados do contato
                                        </div>
                                        <div style={{ color: '#607089', lineHeight: 1.7 }}>
                                            Preencha os campos abaixo para enviar sua mensagem à equipe da RealEnergy.
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label style={labelStyle}>Nome</label>
                                        <div style={{ position: 'relative' }}>
                                            <FiUser
                                                size={18}
                                                style={{
                                                    position: 'absolute',
                                                    left: '14px',
                                                    top: '17px',
                                                    color: '#68809f',
                                                    zIndex: 2,
                                                }}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Digite seu nome"
                                                className="form-control"
                                                value={nome}
                                                onChange={(e) => setNome(e.target.value)}
                                                onBlur={() => markTouched('nome')}
                                                style={{
                                                    ...inputBaseStyle,
                                                    ...getFieldState('nome'),
                                                    paddingLeft: '42px',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label style={labelStyle}>E-mail</label>
                                        <div style={{ position: 'relative' }}>
                                            <FiMail
                                                size={18}
                                                style={{
                                                    position: 'absolute',
                                                    left: '14px',
                                                    top: '17px',
                                                    color: '#68809f',
                                                    zIndex: 2,
                                                }}
                                            />
                                            <input
                                                type="email"
                                                placeholder="seuemail@exemplo.com.br"
                                                className="form-control"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                onBlur={() => markTouched('email')}
                                                style={{
                                                    ...inputBaseStyle,
                                                    ...getFieldState('email'),
                                                    paddingLeft: '42px',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label style={labelStyle}>Telefone</label>
                                        <div style={{ position: 'relative' }}>
                                            <FiPhone
                                                size={18}
                                                style={{
                                                    position: 'absolute',
                                                    left: '14px',
                                                    top: '17px',
                                                    color: '#68809f',
                                                    zIndex: 2,
                                                }}
                                            />
                                            <TelefoneBrasileiroInput
                                                value={telephone}
                                                onChange={(e) => setTelephone(e.target.value)}
                                                onBlur={() => markTouched('telephone')}
                                                temDDD
                                                separaNono
                                                className="form-control"
                                                placeholder="(00)00000-0000"
                                                style={{
                                                    ...inputBaseStyle,
                                                    ...getFieldState('telephone'),
                                                    paddingLeft: '42px',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label style={labelStyle}>Motivo do contato</label>
                                        <select
                                            className="form-select"
                                            value={motivo}
                                            onChange={(e) => setMotivo(e.target.value)}
                                            onBlur={() => markTouched('motivo')}
                                            style={{ ...inputBaseStyle, ...getFieldState('motivo') }}
                                        >
                                            <option value="">Selecione o motivo</option>
                                            <option value="Informações">Informações</option>
                                            <option value="Reclamações">Reclamações</option>
                                            <option value="Elogios">Elogios</option>
                                            <option value="Comercial">Comercial</option>
                                            <option value="Parcerias">Parcerias</option>
                                            <option value="Outros">Outros</option>
                                        </select>
                                    </div>

                                    <div className="col-12">
                                        <label style={labelStyle}>Mensagem</label>
                                        <div style={{ position: 'relative' }}>
                                            <FiMessageSquare
                                                size={18}
                                                style={{
                                                    position: 'absolute',
                                                    left: '14px',
                                                    top: '17px',
                                                    color: '#68809f',
                                                    zIndex: 2,
                                                }}
                                            />
                                            <textarea
                                                placeholder="Escreva sua mensagem com o máximo de clareza possível."
                                                className="form-control"
                                                value={menssage}
                                                onChange={(e) => setMessage(e.target.value)}
                                                onBlur={() => markTouched('menssage')}
                                                style={{
                                                    ...inputBaseStyle,
                                                    ...getFieldState('menssage'),
                                                    minHeight: '180px',
                                                    resize: 'vertical',
                                                    paddingLeft: '42px',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            <div style={{ ...sideCardStyle, marginBottom: '24px' }}>
                                <div
                                    style={{
                                        fontSize: '1.05rem',
                                        fontWeight: 800,
                                        color: '#0d2e63',
                                        marginBottom: '10px',
                                    }}
                                >
                                    Verificações
                                </div>

                                <div style={{ display: 'grid', gap: '12px' }}>
                                    {[
                                        { label: 'Nome preenchido', valid: validation.nome },
                                        { label: 'E-mail válido', valid: validation.email },
                                        { label: 'Motivo selecionado', valid: validation.motivo },
                                        { label: 'Mensagem completa', valid: validation.menssage },
                                        { label: 'Termos aceitos', valid: validation.checkbox },
                                    ].map((item) => (
                                        <div
                                            key={item.label}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                color: item.valid ? '#198754' : '#607089',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {item.valid ? (
                                                <FiCheckCircle size={18} />
                                            ) : (
                                                <FiAlertCircle size={18} />
                                            )}
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={sideCardStyle}>
                                <div
                                    style={{
                                        fontSize: '1.05rem',
                                        fontWeight: 800,
                                        color: '#0d2e63',
                                        marginBottom: '10px',
                                    }}
                                >
                                    Confirmação legal
                                </div>

                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        color: '#4f617a',
                                        lineHeight: 1.7,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checkbox}
                                        onChange={() => {
                                            setCheck(!checkbox);
                                            markTouched('checkbox');
                                        }}
                                        style={{ marginTop: '5px' }}
                                    />
                                    <span>
                                        Eu concordo com os termos de serviço e com a{' '}
                                        <a
                                            style={{
                                                color: '#0d4fb3',
                                                textDecoration: 'none',
                                                fontWeight: 700,
                                            }}
                                            href="/etica"
                                        >
                                            Política de Privacidade
                                        </a>
                                        .
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: '28px',
                            background: '#ffffff',
                            borderRadius: '24px',
                            padding: '24px',
                            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                            border: '1px solid rgba(13, 46, 99, 0.06)',
                        }}
                    >
                        <div className="row align-items-center g-3">
                            <div className="col-lg-8">
                                <div
                                    style={{
                                        fontSize: '1.05rem',
                                        fontWeight: 800,
                                        color: '#0d2e63',
                                        marginBottom: '6px',
                                    }}
                                >
                                    Finalizar envio
                                </div>
                                <div style={{ color: '#607089', lineHeight: 1.8 }}>
                                    Revise sua mensagem e clique abaixo para encaminhar seu contato à equipe da RealEnergy.
                                </div>
                            </div>

                            <div className="col-lg-4 text-lg-end">
                                <motion.button
                                    type="submit"
                                    whileHover={{ y: -2, scale: 1.01 }}
                                    whileTap={{ scale: 0.98 }}
                                    disabled={isSubmitting}
                                    style={{
                                        width: '100%',
                                        maxWidth: '320px',
                                        minHeight: '56px',
                                        border: 'none',
                                        borderRadius: '14px',
                                        background: isSubmitting
                                            ? 'linear-gradient(135deg, #86a8dc, #5e8dd6)'
                                            : 'linear-gradient(135deg, #0d4fb3, #0a3d91)',
                                        color: '#ffffff',
                                        fontWeight: 800,
                                        boxShadow: '0 18px 36px rgba(13, 79, 179, 0.22)',
                                        transition: 'all 0.25s ease',
                                    }}
                                >
                                    {isSubmitting ? 'Enviando mensagem...' : 'Enviar mensagem'}
                                </motion.button>
                            </div>
                        </div>
                    </div>

                    <AnimatePresence>
                        {messageError && (
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 12 }}
                                transition={{ duration: 0.22 }}
                                style={{
                                    marginTop: '22px',
                                    borderRadius: '18px',
                                    padding: '16px 18px',
                                    background: '#fff5f5',
                                    border: '1px solid rgba(220, 53, 69, 0.15)',
                                    color: '#b02a37',
                                    fontWeight: 600,
                                }}
                            >
                                {messageError}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {resultTrue && (
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 12 }}
                                transition={{ duration: 0.22 }}
                                style={{
                                    marginTop: '22px',
                                    borderRadius: '18px',
                                    padding: '16px 18px',
                                    background: '#f3fff7',
                                    border: '1px solid rgba(25, 135, 84, 0.15)',
                                    color: '#146c43',
                                    fontWeight: 600,
                                }}
                            >
                                {resultTrue}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </form>
            </div>
        </section>
    );
}

export default ContactForm;